import LanguageSelector from 'components/commons/LanguageSelector';
import PageContainer from 'containers/pc/PageContainer';
import React, { ReactNode, useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import mainRoutes from 'routes/mainRoutes';
import { Row } from 'saladsoft-primitive';
import styled from 'styled-components';
import PersonIcon from '@mui/icons-material/Person';
import { IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useStoreWrapper } from 'stores/StoreHelper';
import ServiceHelper from 'stores/ServiceHelper';
import { UserInfoDto } from 'services/data-contracts';

const UserLayout = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const { uiStore } = useStoreWrapper();

  const navigate = useNavigate();
  const location = useLocation();
  const currentRoute = mainRoutes.find((route) => route.path === location.pathname);
  const showHeader = currentRoute?.useHeader !== false;
  const userInfoRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfoDto | null>(null);

  const routes = [
    { route: '/title-cleansing', name: t('titleCleansing'), dropdown: false },
    // { route: '/receiver-cleansing', name: t('receiverCleansing'), dropdown: false, ready: true },
    { route: '/businessinfo-cleansing', name: t('businessInfoCleansing'), dropdown: false, ready: true, },
    { route: '/settings', name: t('settings'), role: 'ADMIN' },
  ];

  const getUserInfo = () => {
    ServiceHelper.myAccountService.getMyAccount().then((res) => {
      setUserInfo(res.data);
    });
  };

  const handleLogout = () => {
    uiStore?.confirm.show({
      title: t('logout'),
      okText: t('ok'),
      closeText: t('close'),
      message: t('logoutMessage'),
      onConfirmed: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      },
    });
  };

  useEffect(() => {
    getUserInfo();

    const handleClickOutside = (event: MouseEvent) => {
      if (userInfoRef.current && !userInfoRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userInfo?.isAdmin, location.pathname]);

  return (
    <PageContainer
      style={{ height: '100vh', justifyContent: 'flex-start' }}
      className="user-layout"
    >
      {showHeader && (
        <Row
          justifyContent="space-between"
          padding="16px 10px"
          marginBottom={15}
          backgroundImage="url('/images/loginbg.jpg')"
          backgroundPosition="left"
          backgroundRepeat="no-repeat"
          position="relative"
          zIndex={10}
        >
          <Row gap={100}>
            <LogoStyle>
              <img src="/images/kscustomsai_white.svg" style={{ width: 200 }} />
            </LogoStyle>
            <MenuItem>
              {userInfo?.isAdmin ? (
                <>
                  {routes.map((route) => (
                    <>
                      <LinkStyle
                        key={route.route}
                        to={route.route}
                        $isActive={location.pathname === route.route}
                      >
                        {route.name}
                      </LinkStyle>
                    </>
                  ))}
                </>
              ) : (
                <>
                  {routes
                    .filter((route) => route.role !== 'ADMIN')
                    .map((route) => (
                      <LinkStyle to={route.route} $isActive={location.pathname === route.route}>
                        {route.name}
                      </LinkStyle>
                    ))}
                </>
              )}
            </MenuItem>
          </Row>
          <UserWrapper>
            <div style={{ marginRight: '10px' }}>
              <LanguageSelector />
            </div>
            <UserInfo ref={userInfoRef}>
              <IconButton
                style={{ background: 'rgba(255,255,255,0.3)' }}
                onClick={() => setIsOpen(!isOpen)}
              >
                <PersonIcon style={{ color: '#ffffff' }} />
              </IconButton>
              <UserInfoDropdown
                onClick={() => {
                  handleLogout();
                  setIsOpen(!isOpen);
                }}
                isOpen={isOpen}
              >
                {t('logout')}
              </UserInfoDropdown>
            </UserInfo>
          </UserWrapper>
        </Row>
      )}
      {children}
    </PageContainer>
  );
};

const LogoStyle = styled.div`
  font-size: 24px;
  font-weight: 600;
  padding-left: 10px;
`;

const MenuItem = styled.div`
  display: flex;
  gap: 50px;
`;

const UserWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  z-index: 10;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const UserInfoDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  padding: 10px 20px;
  right: 5px;
  width: 200px;
  border: 1px solid #c7c7c7;
  border-radius: 6px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  align-items: center;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
  transform: ${(props) => (props.isOpen ? 'translateY(20px)' : 'translateY(-20px)')};
  transition: all 0.2s ease-in-out;
  &:hover {
    background: #f9f9f9;
  }
  z-index: 1;
  cursor: pointer;
  margin-top: 5px;
`;

const LinkStyle = styled(Link) <{ $isActive: boolean }>`
  text-decoration: none;
  font-weight: ${(props) => (props.$isActive ? 600 : 400)};
  color: ${(props) => (props.$isActive ? '#ffffff' : '#d6d6d6')};
  padding-bottom: 20px;
  position: relative;
  top: 16px;
  border-bottom: ${(props) => (props.$isActive ? '2px solid yellow' : 'none')};
`;

export default UserLayout;
